import { Injectable } from '@angular/core';
import {
  ILanzaHumedad,
  IQueryParam,
  ICliente,
  ICreateLanzaHumedad,
  IUpdateLanzaHumedad,
  IReporteLanzaHumedad,
  SemaforoConNivel,
} from 'modelos/src';
import { Observable } from 'rxjs';
import { apiBase } from 'src/environments/environment';
import { HelperService } from '../../../auxiliares/helper.service';
import { HttpService } from '../../../auxiliares/http.service';

const api = apiBase;

@Injectable({
  providedIn: 'root',
})
export class LanzaHumedadService {
  constructor(private http: HttpService) {}

  // Lanza Humedad
  public getLanzaHumedad(id: string): Observable<ILanzaHumedad> {
    return this.http.get(`${api}/lanza-humedad/${id}`);
  }
  public getLanzasHumedad(queryParams?: IQueryParam): Observable<{
    data: ILanzaHumedad[];
    total: number;
  }> {
    const params = HelperService.getQueryParams(queryParams);
    return this.http.get(`${api}/lanza-humedad`, { params });
  }
  public createLanzaHumedad(
    data: ICreateLanzaHumedad,
  ): Observable<ILanzaHumedad> {
    return this.http.post(`${api}/lanza-humedad`, data);
  }
  public updateLanzaHumedad(
    id: string,
    data: IUpdateLanzaHumedad,
  ): Observable<ILanzaHumedad> {
    return this.http.put(`${api}/lanza-humedad/${id}`, data);
  }
  public deleteLanzaHumedad(id: string): Observable<ILanzaHumedad> {
    return this.http.delete(`${api}/lanza-humedad/${id}`);
  }

  // Reportes

  public async exportarReportes(
    queryParams?: IQueryParam,
    filename = 'reportes.xlsx',
  ): Promise<void> {
    const params = HelperService.getQueryParams(queryParams);
    await this.http.getFile(
      `${api}/reportes/exportar/lanza-humedad`,
      filename,
      params,
    );
  }

  // Parseos

  public estadoLanzaHumedad(dato: ILanzaHumedad) {
    const limite = this.fechaHaceXHoras(6);
    const ultimaComunicacion = dato.ultimaComunicacion
      ? new Date(dato.ultimaComunicacion)
      : null;

    if (!ultimaComunicacion || limite > ultimaComunicacion) {
      return 'Sin reportar';
    }

    return dato.ultimoEstado;
  }

  private fechaHaceXHoras(horas: number) {
    const fecha = new Date();
    fecha.setHours(fecha.getHours() - horas);
    return fecha;
  }

  /**
   * Obtiene el color para un nivel específico basado en el valor de humedad
   */
  public obtenerColorPorNivel(
    lanzaHumedad: ILanzaHumedad,
    numeroSensor: any,
    valorHumedad: any,
    cliente?: ICliente,
  ): string | undefined {
    // Buscar configuración del nivel específico
    const coloresNivel =
      lanzaHumedad?.configuraciones?.coloresNivel ||
      cliente?.configuraciones?.humedadSuelo?.coloresNivel;

    if (!coloresNivel) return undefined;
    if (numeroSensor === undefined || numeroSensor === null) return undefined;
    if (valorHumedad === undefined || valorHumedad === null) return undefined;

    // ✅ FILTRAR y buscar solo SemaforoConNivel (que tienen propiedad 'nivel')
    const configNivel = coloresNivel
      .filter((config): config is SemaforoConNivel => 'nivel' in config)
      .find((config) => config.nivel === numeroSensor);

    if (!configNivel) return undefined;

    // Verificar si el valor está en el rango del semáforo
    const semaforo = configNivel.semaforo;
    if (
      valorHumedad >= semaforo.valorDesde &&
      valorHumedad < semaforo.valorHasta
    ) {
      return semaforo.color;
    }

    return undefined;
  }

  /**
   * Obtiene todos los colores para todos los niveles de un reporte
   */
  public obtenerColoresPorReporte(
    lanzaHumedad: ILanzaHumedad,
    reporte: IReporteLanzaHumedad,
    cliente?: ICliente,
  ): { [nivel: number]: string } {
    const colores: { [nivel: number]: string } = {};

    if (!reporte?.humedad || typeof reporte.humedad !== 'object') {
      console.warn('No hay datos de humedad en el reporte');
      return colores;
    }

    // Iterar sobre cada sensor en el reporte
    Object.entries(reporte.humedad).forEach(([nroSensor, valorHumedad]) => {
      try {
        const nivel = parseInt(nroSensor);

        // ✅ VALIDAR que nivel y valor son válidos
        if (isNaN(nivel) || typeof valorHumedad !== 'number') {
          console.warn(
            `Datos inválidos para sensor ${nroSensor}:`,
            valorHumedad,
          );
          return;
        }

        const color = this.obtenerColorPorNivel(
          lanzaHumedad,
          nivel,
          valorHumedad,
          cliente,
        );

        if (color) {
          colores[nivel] = color;
        }
      } catch (error) {
        console.error(`Error procesando sensor ${nroSensor}:`, error);
      }
    });

    return colores;
  }

  public ordenarPorNumero = (a: any, b: any): number => {
    return +a.key - +b.key;
  };
}
